import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../context/ToastContext';
import {
  acceptReceivedBid,
  getReceivedBids,
  getSubmittedBids,
  rejectReceivedBid,
} from '../../../services/bidApi';

const STATUS_META = {
  PENDING: {
    label: 'Bekliyor',
    icon: 'Clock3',
    badgeClass: 'bg-warning-100 text-warning',
    cardClass: 'border-warning bg-background',
  },
  ACCEPTED: {
    label: 'Kabul Edildi',
    icon: 'BadgeCheck',
    badgeClass: 'bg-success-100 text-success',
    cardClass: 'border-success bg-success-50/40',
  },
  REJECTED: {
    label: 'Reddedildi',
    icon: 'XCircle',
    badgeClass: 'bg-error-100 text-error',
    cardClass: 'border-error bg-error-50/40',
  },
  WITHDRAWN: {
    label: 'Geri Çekildi',
    icon: 'ArrowLeftCircle',
    badgeClass: 'bg-secondary-100 text-text-secondary',
    cardClass: 'border-border bg-background',
  },
};

const TAB_META = {
  received: {
    label: 'Gelen Teklifler',
    icon: 'Inbox',
    emptyTitle: 'Henüz gelen teklif yok',
    emptyDescription: 'Ürünlerine yeni teklifler geldikçe burada değerlendirebilirsin.',
  },
  submitted: {
    label: 'Gönderdiğim Teklifler',
    icon: 'Send',
    emptyTitle: 'Henüz gönderilmiş teklif yok',
    emptyDescription: 'Gönderdiğin tüm teklifler ve güncel durumları bu listede görünür.',
  },
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatQuantity = (value, unit) =>
  `${Number(value || 0).toLocaleString('tr-TR')} ${unit || ''}`.trim();

const sortBids = (items) =>
  [...items].sort((left, right) => {
    const leftPending = left?.status === 'PENDING' ? 0 : 1;
    const rightPending = right?.status === 'PENDING' ? 0 : 1;
    if (leftPending !== rightPending) {
      return leftPending - rightPending;
    }

    return new Date(right?.createdAt || 0).getTime() - new Date(left?.createdAt || 0).getTime();
  });

const SummaryPill = ({ icon, label, value, className }) => (
  <div
    className={`inline-flex w-full min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-xs font-medium ${className}`}
  >
    <Icon name={icon} size={15} />
    <span>{value}</span>
    <span>{label}</span>
  </div>
);

const DetailCell = ({ icon, label, value, valueClassName = '' }) => (
  <div className="flex items-start gap-3 px-4 py-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-text-secondary">
      <Icon name={icon} size={17} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-medium leading-5 text-text-secondary">{label}</p>
      <p
        className={`mt-0.5 text-sm font-semibold leading-5 text-text-primary break-words ${valueClassName}`.trim()}
      >
        {value}
      </p>
    </div>
  </div>
);

const BidManagement = ({ refreshKey = 0, onBidActionComplete }) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('received');
  const [receivedBids, setReceivedBids] = useState([]);
  const [submittedBids, setSubmittedBids] = useState([]);
  const [acceptQuantityById, setAcceptQuantityById] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingActionById, setPendingActionById] = useState({});

  const loadBids = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const [receivedResponse, submittedResponse] = await Promise.all([
        getReceivedBids(),
        getSubmittedBids(),
      ]);

      const nextReceived = Array.isArray(receivedResponse)
        ? receivedResponse
        : receivedResponse?.items || [];
      const nextSubmitted = Array.isArray(submittedResponse)
        ? submittedResponse
        : submittedResponse?.items || [];

      setReceivedBids(nextReceived);
      setSubmittedBids(nextSubmitted);
      setAcceptQuantityById((prev) => {
        const next = { ...prev };
        nextReceived.forEach((bid) => {
          if (next[bid.id] === undefined) {
            next[bid.id] = `${bid?.quantity || ''}`;
          }
        });
        return next;
      });
    } catch (error) {
      const message = error?.message || 'Teklifler yüklenemedi.';
      setErrorMessage(message);
      toastError(message);
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    loadBids();
  }, [loadBids, refreshKey]);

  const sortedReceivedBids = useMemo(() => sortBids(receivedBids), [receivedBids]);
  const sortedSubmittedBids = useMemo(() => sortBids(submittedBids), [submittedBids]);
  const items = activeTab === 'received' ? sortedReceivedBids : sortedSubmittedBids;

  const summary = useMemo(() => {
    const pendingReceived = receivedBids.filter((bid) => bid?.status === 'PENDING').length;
    const acceptedSubmitted = submittedBids.filter((bid) => bid?.status === 'ACCEPTED').length;
    const rejectedSubmitted = submittedBids.filter((bid) => bid?.status === 'REJECTED').length;

    return {
      pendingReceived,
      acceptedSubmitted,
      rejectedSubmitted,
      totalReceived: receivedBids.length,
      totalSubmitted: submittedBids.length,
    };
  }, [receivedBids, submittedBids]);

  const tabCounts = {
    received: summary.totalReceived,
    submitted: summary.totalSubmitted,
  };

  const setPendingAction = (bidId, value) => {
    setPendingActionById((prev) => ({
      ...prev,
      [bidId]: value,
    }));
  };

  const clearPendingAction = (bidId) => {
    setPendingActionById((prev) => {
      const next = { ...prev };
      delete next[bidId];
      return next;
    });
  };

  const handleAccept = async (bid) => {
    const rawValue = `${acceptQuantityById[bid.id] ?? bid.quantity ?? ''}`.trim();
    const acceptedQuantity = Number(rawValue);

    if (!Number.isInteger(acceptedQuantity) || acceptedQuantity < 1) {
      toastError('Kabul miktarı en az 1 olmalı.');
      return;
    }

    setPendingAction(bid.id, 'accept');

    try {
      const updated = await acceptReceivedBid(bid.id, { acceptedQuantity });
      toastSuccess('Teklif kabul edildi.');
      await loadBids();
      onBidActionComplete?.(updated);
    } catch (error) {
      toastError(error?.message || 'Teklif kabul edilemedi.');
    } finally {
      clearPendingAction(bid.id);
    }
  };

  const handleReject = async (bid) => {
    setPendingAction(bid.id, 'reject');

    try {
      const updated = await rejectReceivedBid(bid.id);
      toastSuccess('Teklif reddedildi.');
      await loadBids();
      onBidActionComplete?.(updated);
    } catch (error) {
      toastError(error?.message || 'Teklif reddedilemedi.');
    } finally {
      clearPendingAction(bid.id);
    }
  };

  const renderEmptyState = () => {
    const tabMeta = TAB_META[activeTab];

    return (
      <div className="rounded-xl border border-dashed border-border bg-background px-6 py-14 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-text-secondary">
          <Icon name={tabMeta.icon} size={24} />
        </div>
        <h4 className="mt-4 text-lg font-semibold text-text-primary">{tabMeta.emptyTitle}</h4>
        <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
          {tabMeta.emptyDescription}
        </p>
      </div>
    );
  };

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface shadow-elevation-1">
      <div className="border-b border-border p-6">
        <div className="flex flex-col gap-4">
          <div className="max-w-3xl">
            <h3 className="text-lg font-semibold text-text-primary font-heading">
              Teklif Yönetimi
            </h3>
            <p className="mt-2 text-sm leading-6 text-text-secondary">
              Gelen teklifleri değerlendir, gönderdiklerinin durumunu takip et ve kabul kararlarını
              stokla birlikte aynı ekrandan yönet.
            </p>
          </div>

          <div className="grid w-full gap-2 lg:grid-cols-3">
            <SummaryPill
              icon="Clock3"
              label="bekleyen gelen teklif"
              value={summary.pendingReceived}
              className="bg-warning-100 text-warning"
            />
            <SummaryPill
              icon="BadgeCheck"
              label="kabul edilen teklif"
              value={summary.acceptedSubmitted}
              className="bg-success-100 text-success"
            />
            <SummaryPill
              icon="XCircle"
              label="reddedilen teklif"
              value={summary.rejectedSubmitted}
              className="bg-error-100 text-error"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-text-secondary">
            <Icon name="Loader2" size={22} className="animate-spin" />
          </div>
          <p className="mt-4 text-sm text-text-secondary">Teklifler yükleniyor...</p>
        </div>
      ) : errorMessage ? (
        <div className="p-6">
          <div className="rounded-xl border border-error-100 bg-error-50 px-6 py-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-error">
              <Icon name="AlertTriangle" size={22} />
            </div>
            <h4 className="mt-4 text-lg font-semibold text-text-primary">Teklifler yüklenemedi</h4>
            <p className="mt-2 text-sm text-text-secondary">{errorMessage}</p>
            <button
              type="button"
              onClick={loadBids}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-primary-700"
            >
              <Icon name="RefreshCw" size={16} />
              Tekrar Dene
            </button>
          </div>
        </div>
      ) : (
        <div className="p-6">
          <div className="mb-6 inline-flex rounded-xl border border-border bg-secondary-100 p-1">
            {Object.entries(TAB_META).map(([key, tab]) => {
              const isActive = activeTab === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                  <span
                    className={`rounded-md px-2 py-0.5 text-xs ${
                      isActive ? 'bg-white/20 text-white' : 'bg-surface text-text-secondary'
                    }`}
                  >
                    {tabCounts[key]}
                  </span>
                </button>
              );
            })}
          </div>

          {items.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="space-y-4">
              {items.map((bid) => {
                const statusMeta = STATUS_META[bid?.status] || STATUS_META.PENDING;
                const pendingAction = pendingActionById[bid.id];
                const maxAcceptedQuantity = Math.max(
                  1,
                  Math.min(
                    Number(bid?.quantity || 0),
                    Number(bid?.product?.availableQuantity || bid?.quantity || 0),
                  ),
                );

                const primaryPartyLabel = activeTab === 'received' ? 'Teklif Veren' : 'Satıcı';
                const primaryPartyValue =
                  activeTab === 'received'
                    ? bid?.buyer?.fullName || bid?.buyer?.email || '-'
                    : bid?.seller?.fullName || bid?.seller?.email || '-';
                const detailRows = [
                  [
                    {
                      icon: 'UserRound',
                      label: primaryPartyLabel,
                      value: primaryPartyValue,
                    },
                    {
                      icon: 'BadgeDollarSign',
                      label: 'Birim Fiyat',
                      value: formatCurrency(bid?.amount),
                    },
                  ],
                  [
                    {
                      icon: 'Package',
                      label: 'İstenen Miktar',
                      value: formatQuantity(bid?.quantity, bid?.product?.unit),
                    },
                    {
                      icon: 'Boxes',
                      label: 'Kalan Stok',
                      value: formatQuantity(bid?.product?.availableQuantity, bid?.product?.unit),
                    },
                  ],
                  [
                    {
                      icon: 'Wallet',
                      label: 'Toplam Teklif',
                      value: formatCurrency(bid?.totalAmount),
                    },
                    {
                      icon: 'CalendarClock',
                      label: 'Oluşturulma',
                      value: formatDate(bid?.createdAt),
                    },
                  ],
                ];
                if (bid?.note) {
                  detailRows.push([
                    {
                      icon: 'ScrollText',
                      label: 'Not',
                      value: bid.note,
                      valueClassName: 'text-sm font-medium text-text-primary',
                    },
                  ]);
                }

                return (
                  <article
                    key={bid.id}
                    className={`rounded-xl border p-5 transition-all duration-200 hover:shadow-elevation-2 ${statusMeta.cardClass}`}
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <h4 className="text-lg font-semibold leading-tight text-text-primary break-words">
                          {bid?.product?.title || 'İsimsiz Ürün'}
                        </h4>
                      </div>

                      <span
                        className={`inline-flex items-center gap-2 self-start rounded-md px-3 py-1.5 text-xs font-medium ${statusMeta.badgeClass}`}
                      >
                        <Icon name={statusMeta.icon} size={15} />
                        {statusMeta.label}
                      </span>
                    </div>

                    <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface">
                      {detailRows.map((row, rowIndex) => (
                        <div
                          key={`${bid.id}-row-${rowIndex}`}
                          className={`${rowIndex > 0 ? 'border-t border-border' : ''} grid md:grid-cols-2`}
                        >
                          {row.map((item, itemIndex) => (
                            <div
                              key={`${bid.id}-${item.label}`}
                              className={`${itemIndex > 0 ? 'md:border-l md:border-border' : ''} ${row.length === 1 ? 'md:col-span-2' : ''}`}
                            >
                              <DetailCell
                                icon={item.icon}
                                label={item.label}
                                value={item.value}
                                valueClassName={item.valueClassName}
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    {bid?.acceptedQuantity ? (
                      <div className="mt-4 border-t border-border pt-4">
                        <div className="rounded-md border border-success-100 bg-success-50 px-4 py-3 text-sm text-success">
                          <div className="flex flex-wrap items-center gap-2">
                            <Icon name="BadgeCheck" size={16} />
                            <span className="font-semibold">
                              Kabul edilen miktar:
                              {' '}
                              {formatQuantity(bid?.acceptedQuantity, bid?.product?.unit)}
                            </span>
                            <span className="text-success/80">•</span>
                            <span>Toplam: {formatCurrency(bid?.acceptedTotalAmount)}</span>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {activeTab === 'received' && bid?.status === 'PENDING' ? (
                      <div className="mt-4 border-t border-border pt-4">
                        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px] xl:items-end">
                          <div>
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary">
                                <Icon name="SlidersHorizontal" size={18} />
                              </div>
                              <div>
                                <h5 className="text-sm font-medium text-text-primary">
                                  Hızlı Karar
                                </h5>
                                <p className="text-sm text-text-secondary">
                                  Kabul miktarını belirleyip teklifi hemen sonuçlandır.
                                </p>
                              </div>
                            </div>

                            <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
                              <label className="block">
                                <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-text-secondary">
                                  Kabul Miktarı
                                </span>
                                <input
                                  type="number"
                                  min="1"
                                  max={maxAcceptedQuantity}
                                  value={acceptQuantityById[bid.id] ?? `${bid?.quantity || ''}`}
                                  onChange={(event) =>
                                    setAcceptQuantityById((prev) => ({
                                      ...prev,
                                      [bid.id]: event.target.value,
                                    }))
                                  }
                                  className="mt-2 w-full rounded-md border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-primary focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                />
                              </label>

                              <div className="rounded-md border border-border bg-secondary-50 px-4 py-2.5">
                                <p className="text-xs font-medium text-text-secondary">
                                  Maksimum Kabul
                                </p>
                                <p className="mt-2 text-sm font-medium text-text-primary">
                                  {formatQuantity(maxAcceptedQuantity, bid?.product?.unit)}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => handleAccept(bid)}
                              disabled={Boolean(pendingAction)}
                              className="inline-flex items-center justify-center gap-2 rounded-md bg-success px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                              {pendingAction === 'accept' ? (
                                <Icon name="Loader2" size={16} className="animate-spin" />
                              ) : (
                                <Icon name="Check" size={16} />
                              )}
                              <span>Kabul Et</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(bid)}
                              disabled={Boolean(pendingAction)}
                              className="inline-flex items-center justify-center gap-2 rounded-md bg-error px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                              {pendingAction === 'reject' ? (
                                <Icon name="Loader2" size={16} className="animate-spin" />
                              ) : (
                                <Icon name="X" size={16} />
                              )}
                              <span>Reddet</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default BidManagement;
